import { TZDate } from '@date-fns/tz';
import { Placement } from '@floating-ui/dom';
import { add } from 'date-fns/add';
import { format } from 'date-fns/format';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { isEqual } from 'date-fns/isEqual';
import type { Locale } from 'date-fns/locale';
import { enUS } from 'date-fns/locale';
import { parse } from 'date-fns/parse';
import { setDefaultOptions } from 'date-fns/setDefaultOptions';
import { createFocusTrap, FocusTrap } from 'focus-trap';
import { html, nothing, PropertyValueMap, TemplateResult, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { DirectiveResult } from 'lit/directive';
import { ClassMapDirective } from 'lit/directives/class-map';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { AutoPositionController } from '../../controllers/autoPositionOverlay';
import { BlocksDateRangeEvent } from '../../events';
import { BlocksCloseEvent } from '../../events/blocks-close';
import { BlocksOpenEvent } from '../../events/blocks-open';
import type { AutoPositionInterface, Position } from '../../util/common-modal';
import { AllowedPlacements } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { isPopoverSupported } from '../../util/popover';
import { BlocksElement } from '../BlocksElement';
import '../button/button';
import { BlocksButton } from '../button/button';
import '../calendar/calendar';
import { BlocksCalendar } from '../calendar/calendar';
import { getConvertedDate, getTimezoneConfig } from '../calendar/date-utils';
import '../combined-input/combined-input';
import type { DateFieldPosition, DateFieldSizeOptions, DatePickerMessageType } from '../daterangepicker-field/daterangepicker-field.model';
import '../input-field/input-field';
import { BlocksInputField } from '../input-field/input-field';
import '../time-selector-list/time-selector-list';
import { BlocksTimeSelectorList } from '../time-selector-list/time-selector-list';
import { BlocksTooltipLabelOverflow } from '../tooltip-label-overflow/tooltip-label-overflow';
import styles from './daterangepicker-field.styles';

type TimeFormat = 'HH:mm' | 'h:mm a';

/**
 * @slot field-note - The dropdown field note
 * @slot error - The dropdown error note
 * @slot warning - The dropdown warning note
 * @fires bl-date-range-open - emitted when daterange-picker is opened
 * @fires bl-date-range-close - emitted when daterange-picker is closed
 * @fires bl-date-range-change - emitted when date range is changed
 * @csspart base - The component's base wrapper.
 * @csspart loader-container - The component's loader-container.
 * @csspart loader-wrapper - The component's loader wrapper.
 * @csspart label-loader - The component's label loader.
 * @csspart label-skeleton - The component's label skeleton.
 * @csspart body-loader - The component's body loader.
 * @csspart body-skeleton - The component's body skeleton.
 * @csspart icon-calendar - The component's calendar icon.
 * @csspart field-note-loader - The component's field-note-loader.
 * @csspart field-note-skeleton - The component's field-note-skeleton.
 * @csspart body - The component's body wrapper.
 * @csspart combined - The component's `<cat-combined-input>`.
 * @csspart popup-wrapper - The component's popup wrapper.
 * @csspart popup - The component's popup.
 * @csspart popup-body - The component's popup body.
 * @csspart calendar-start-wrapper - The component's start calendar wrapper.
 * @csspart calendar-start - The component's start `<cat-calendar>`.
 * @csspart time-start-wrapper - The component's start time wrapper.
 * @csspart time-start - The component's start `<cat-time-selector-list>`.
 * @csspart calendar-end-wrapper - The component's end calendar wrapper.
 * @csspart calendar-end - The component's end `<cat-calendar>`.
 * @csspart time-end-wrapper - The component's end time wrapper.
 * @csspart time-end - The component's end `<cat-time-selector-list>`.
 * @csspart footer - The component's footer wrapper.
 * @csspart footer-submit - The component's footer-submit button.
 * @csspart footer-cancel - The component's footer-cancel button.
 * @csspart field-note-wrapper - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 * @csspart part-error - The component's error slot.
 * @csspart part-warning - The component's warning slot.
 */
export class BlocksDaterangepickerField extends BlocksElement implements AutoPositionInterface {
  static formAssociated = true;static styles: CSSResultGroup = styles;

  /**
   * locale object from date-fns/locale
   */
  @property({ type: Object })
  locale: Locale = enUS;

  /**
   * The unique id of the select field
   */
  @property({ type: String })
  fieldId: string | undefined;

  /**
   * The field note
   */
  @property({ type: String })
  fieldNote: string | undefined;

  /**
   * The error note
   */
  @property({ type: String })
  errorNote: string | undefined;

  /**
   * The dropdown warning note
   */
  @property({ type: String })
  warningNote: string | undefined;

  /**
   * Hide label?
   */
  @property({ type: Boolean })
  hideLabel: boolean | undefined;

  /**
   * ISO Format for date
   */
  @property({ type: String })
  isoFormat = 'yyyy/MM/dd';

  /**
   * Minimum end time for disabled times display (24 hour format, e.g., disabledMinTime="16:00" for 4PM)
   */
  @property({ type: String })
  minEndTime: string | undefined | null;

  /**
   * Use 24 hour time format
   */
  @property({ type: Boolean })
  is24HourFormat = false;

  /**
   * Increments of time
   */
  @property({ type: Number })
  timeIncrements = 30;

  /**
   * Minimum time for between times display (24 hour format, e.g., timeStart=${16:30} for 4:30PM)
   */
  @property({ type: String })
  timeStart: string | undefined;

  /**
   * Maximum time for between times display (24 hour format, e.g., timeEnd=${16:30} for 4:30PM)
   */
  @property({ type: String })
  timeEnd: string | undefined;

  /**
   * Aria describedby
   * 1) Used to connect the field note in field to the input for accessibility
   */
  @property({ type: String })
  ariaDescribedBy: string | undefined;

  /**
   * Cancel button label
   */
  @property({ type: String })
  cancelLabel = 'Cancel';

  /**
   * Apply button label
   */
  @property({ type: String })
  applyLabel = 'Apply';

  /**
   * Time selector heading label
   */
  @property({ type: String })
  timeSelectorLabel = 'Time';

  /**
   * Disabled attribute on the input
   */
  @property({ type: Boolean, reflect: true })
  disabled: boolean | undefined;

  /**
   * Required attribute on the input
   */
  @property({ type: Boolean })
  required: boolean | undefined;

  /**
   * If the window is a small screen
   */
  @property({ type: Boolean })
  isSmallScreen = false;

  /**
   * Show/hide datepicker
   */
  @property({ type: Boolean })
  showDateRangePicker: boolean | undefined;

  /**
   * Add time selector to date picker
   */
  @property({ type: Boolean })
  showTimeSelector: boolean | undefined;

  /**
   * Date Range Picker with full width
   */
  @property({ type: Boolean })
  isFullWidth: boolean | undefined;

  /**
   * Show footer with Cancel/Apply buttons
   */
  @property({ type: Boolean })
  showFooter: boolean | undefined;

  /**
   * Set a specific start date to be active
   */
  @property({ type: String })
  setActiveStartDate: string | undefined | null;

  /**
   * Set a specific end date to be active
   */
  @property({ type: String })
  setActiveEndDate: string | undefined | null;

  /**
   * Set a specific start time to be active (24 hour format, e.g., setActiveTime="16:00" for 4PM)
   */
  @property({ type: String })
  setActiveStartTime: string | undefined | null;

  /**
   * Set a specific end time to be active (24 hour format, e.g., setActiveTime="16:00" for 4PM)
   */
  @property({ type: String })
  setActiveEndTime: string | undefined | null;

  /**
   * The field's placeholder attribute
   */
  @property({ type: String })
  placeholder: string | undefined;

  /**
   * The Start Date field's label
   */
  @property({ type: String })
  startDateLabel = 'Start Date';

  /**
   * The End Date field's label
   */
  @property({ type: String })
  endDateLabel = 'End Date';

  /**
   * Specify date format for UI display
   */
  @property({ type: String })
  dateFormat = 'MMM dd, yyyy';

  /**
   * Minimum date for disabled dates
   */
  @property({ type: String })
  disabledMinDate: string | undefined;

  /**
   * Maximum date for disabled dates
   */
  @property({ type: String })
  disabledMaxDate: string | undefined;

  /**
   * Amount of years to go before and after the current date
   */
  @property({ type: Number })
  multiYear = 3;

  @property({ type: String })
  previousButtonText = 'Previous Month';

  @property({ type: String })
  nextButtonText = 'Next Month';

  /**
   * Positions the dropdown panel absolutely to the datepicker.
   * If `isInline` is false, and if specified position doesn't fit inside the viewport,
   * it will try to accommodate in other positions which fits best.
   */
  @property({ type: String })
  position: DateFieldPosition = 'auto';

  /**
   * Determines the behavior of the panel based on the value of the `isInline` property.
   * If `isInline` is false and the popover attribute is supported by the browser,
   * the panel will be opened inside the top layer.
   * If none of the position fits and if it goes out of viewport, scroll will be enabled if `isInline` is false.
   * If `isInline` is true, the popover attribute will not be used, and the panel
   * will use the floating UI absolute strategy.
   *  @property {Boolean} isInline - Default is false.
   */
  @property({ type: Boolean })
  isInline = false;

  /**
   * size
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**lg** increases the datepicker height with label inside</li>
   * <li>**md** reduce the datepicker height with label outside </li>
   * <li>**sm** reduce  the datepicker height without label</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  size: DateFieldSizeOptions = 'lg';

  /**
   * Status of datepicker input field
   */
  @property({ type: String })
  status: DatePickerMessageType;

  /**
   * Readonly attribute
   */
  @property({ type: Boolean })
  readonly: boolean | undefined;

  /**
   * Loading state
   * 1) Adds a loader if is true
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * Notation is used for setting required or optional label
   */
  @property({ type: String })
  notation = '(required)';

  /**
   * Aria label attribute
   * - Provides an accessible name for the daterangepicker element
   */
  @property({ type: String })
  arialabel = 'Date range picker';

  /**
   * Delay for show tooltip when label overflow
   */
  @property({ type: Number })
  tooltipShowDelay = 0;

  /**
   * Delay for hide tooltip when label overflow
   */
  @property({ type: Number })
  tooltipHideDelay = 0;

  /**
   * Hide the label overflow tooltip
   */
  @property({ type: Boolean })
  hideLabelTooltip = false;

  /**
   * The `timeZone` property represents the time zone that will be passed to the `TZDate` object.
   * This property accepts a string that can either be:
   * - **An IANA time zone identifier** (e.g., `'America/New_York'`, `'Europe/London'`, `'Asia/Kolkata'`).
   * - **A UTC offset string** (e.g., `'-0800'` for UTC-8 hours, or `'+0530'` for UTC+5:30 hours).
   *
   * If not provided, the default value will be `undefined`, and the system's local time zone will be used.
   **/
  @property({ type: String })
  timeZone: string | undefined;

  /**
   * The start field's value attribute
   */
  @state()
  private startValue: string | undefined | null;

  /**
   * The end field's value attribute
   */
  @state()
  private endValue: string | undefined | null;

  /**
   * Check to see if the date selections have been cancelled
   */
  @state()
  private isCancelled = false;

  /**
   * The submit button's attribute
   */
  @state()
  private disabledSubmit = true;

  /**
   * Saved start input full value
   */
  @state()
  private originalStartValue: string | undefined | null;

  /**
   * Saved end input full value
   */
  @state()
  private originalEndValue: string | undefined | null;

  /**
   * Saved start input date value
   */
  @state()
  private originalStartDateFormatted: string | undefined | null;

  /**
   * Saved end input date value
   */
  @state()
  private originalEndDateFormatted: string | undefined;

  /**
   * Saved start time selection
   */
  @state()
  private originalStartTime: Date | undefined | null;

  /**
   * Saved end time selection
   */
  @state()
  private originalEndTime: Date | undefined | null;

  /**
   * Saved start input date value in date format
   */
  @state()
  private originalStartValueRaw: Date | undefined | null;

  /**
   * Saved end input date value in date format
   */
  @state()
  private originalEndValueRaw: Date | undefined | null;

  /**
   * The start field's raw date value
   */
  @state()
  private startValueRaw: Date | undefined | null;

  /**
   * The end field's raw date value
   */
  @state()
  private endValueRaw: Date | undefined | null;

  /**
   * Start time value
   */
  @state()
  private selectedStartTime: Date | undefined | null;

  /**
   * End time value
   */
  @state()
  private selectedEndTime: Date | undefined | null;

  /**
   * Query the combined input container
   */
  @query('.cat-c-daterangepicker-field__body')
  private _catDateInputContainer: HTMLElement | undefined;

  /**
   * Query the date calendar popup
   */
  @query('.cat-c-daterangepicker-field__popup-wrapper')
  private _catDateCalendarPopup: HTMLElement | undefined;

  /**
   * Query the start calendar
   */
  @query('.cat-c-daterangepicker-field__calendar-start')
  private _catDateStartCalendarPopup: HTMLElement | undefined;

  /**
   * Query the start calendar
   */
  @query('.cat-c-daterangepicker-field__calendar-end')
  private _catDateEndCalendarPopup: HTMLElement | undefined;

  /**
   * Get the start calender instance
   */
  private get _catStartCalender(): BlocksCalendar | undefined | null {
    return this._catDateStartCalendarPopup?.querySelector<BlocksCalendar>('cat-calendar');
  }

  /**
   * Get the end calender instance
   */
  private get _catEndCalender(): BlocksCalendar | undefined | null {
    return this._catDateEndCalendarPopup?.querySelector<BlocksCalendar>('cat-calendar');
  }

  /**
   * Get the start time list instance
   */
  private get _catStartTimeList(): BlocksTimeSelectorList | undefined | null {
    return this._catDateStartCalendarPopup?.querySelector('cat-time-selector-list');
  }

  /**
   * Get the end time list instance
   */
  private get _catEndTimeList(): BlocksTimeSelectorList | undefined | null {
    return this._catDateEndCalendarPopup?.querySelector('cat-time-selector-list');
  }

  /**
   * Associates the control with a form element
   */
  get form(): HTMLFormElement | null {
    return this.internals_?.form;
  }

  /**
   * Query the start combined input
   */
  private get _catDateStartInput(): BlocksInputField | null | undefined {
    return this.shadowRoot
      ?.querySelector('cat-combined-input')
      ?.shadowRoot?.querySelector<BlocksInputField>('.cat-c-combined-input__input-container:first-child cat-input-field');
  }

  /**
   * Get the label overflow tooltip
   */
  private get labelOverflowStartTooltip(): BlocksTooltipLabelOverflow | undefined | null {
    return this._catDateStartInput?.shadowRoot?.querySelector('cat-tooltip-label-overflow');
  }

  /**
   * Get the label overflow tooltip
   */
  private get labelOverflowEndTooltip(): BlocksTooltipLabelOverflow | undefined | null {
    return this._catDateEndInput?.shadowRoot?.querySelector('cat-tooltip-label-overflow');
  }

  /**
   * Query the end combined input
   */
  private get _catDateEndInput(): BlocksInputField | undefined | null {
    return this.shadowRoot
      ?.querySelector('cat-combined-input')
      ?.shadowRoot?.querySelector<BlocksInputField>('.cat-c-combined-input__input-container:last-child cat-input-field');
  }

  /**
   * get current time format
   */
  private get timeFormat(): TimeFormat {
    return this.is24HourFormat ? 'HH:mm' : 'h:mm a';
  }

  /**
   * Query all the input fields in the combined input
   */
  private get _catCombinedInputFields(): Array<BlocksInputField> | undefined {
    const combinedInputElements = this.shadowRoot?.querySelector('cat-combined-input');
    const inputFieldElements = combinedInputElements?.shadowRoot?.querySelectorAll<BlocksInputField>('cat-input-field');
    let elements;
    if (inputFieldElements) {
      elements = Array.from(inputFieldElements);
    }
    return elements;
  }

  private get hasStartAndEndDates() {
    return this.startValue && this.endValue;
  }

  private get hasDateAndTime() {
    return (this.selectedStartTime || this.setActiveStartTime) && (this.selectedEndTime || this.setActiveEndTime) && this.hasStartAndEndDates;
  }
  /**
   * Query the combined input container that is active
   */
  private get _catCombinedInputActive() {
    return this._catCombinedInputFields
      ?.filter((el: BlocksInputField) => el.isActive === true)[0]
      ?.shadowRoot?.querySelector<HTMLInputElement>('.cat-c-input-field__input');
  }

  /**
   * Query the combined input container that is not active
   */
  private get _catCombinedInputNotActive() {
    return this._catCombinedInputFields
      ?.filter((el: BlocksInputField) => el.isActive !== true)[0]
      .shadowRoot?.querySelector<HTMLInputElement>('.cat-c-input-field__input');
  }

  /**
   * Query the start time selector
   */
  @query('.cat-c-daterangepicker-field__calendar-start cat-time-selector-list')
  private _catDateStartCalendarTimeList: HTMLElement | undefined;

  /**
   * Query the end time selector
   */
  @query('.cat-c-daterangepicker-field__calendar-end cat-time-selector-list')
  private _catDateEndCalendarTimeList: HTMLElement | undefined;

  @query('.cat-c-daterangepicker-field__popup-body')
  private _catCalendarPanel: HTMLElement | undefined;

  /*
   * Tab focus trap
   */
  private _focusTrap: FocusTrap | undefined;

  private isPopOverSupported = isPopoverSupported();

  private get isPopoverEnabled(): boolean {
    return this.isPopOverSupported && !this.isInline;
  }

  /**
   * Query the Apply "submit" button
   */
  private get _catDateApply(): HTMLElement | null | undefined {
    return this.shadowRoot?.querySelector<BlocksButton>('.cat-c-daterangepicker-field__footer-submit');
  }

  public getFloatingElement(): HTMLElement {
    return this._catDateCalendarPopup!;
  }
  public getPosition(): Position {
    return this.position;
  }
  public getIsInline(): boolean {
    return this.isInline;
  }
  public getReferenceElement(): HTMLElement {
    return this._catDateInputContainer!;
  }
  public getOverflowElement(): HTMLElement | null {
    return this._catDateCalendarPopup!;
  }
  public getAllowedPlacements(): Placement[] {
    return AllowedPlacements;
  }

  private _startMin?: string | null;
  private _startMax?: string | null;
  private _endMin?: string | null;
  private _endMax?: string | null;
  private internals_ = this.attachInternals?.();
  protected autoPositionController = new AutoPositionController(this);

  /**
   * Set tooltip display based on active panel
   */
  private setTooltipDisplay(enableTooltip: boolean) {
    if (!this.hideLabelTooltip) {
      this.displayStartTooltip(enableTooltip);
      this.displayEndTooltip(enableTooltip);
    }
  }

  /**
   * Toggle display of datepicker
   */
  private toggleDateRangePicker = () => {
    if (this.disabled) {
      return;
    }
    this.showDateRangePicker = true;
    this.dynamicPosition();

    this._startMax = this.disabledMaxDate || null;
    this._startMin = this.disabledMinDate || null;

    this._endMax = this.disabledMaxDate || null;
    this._endMin = this.disabledMinDate || null;

    /**
     * Disable tabbing on combined input that is not active, so the next tab sequence would be inside the datepicker popup.
     */
    if (this._catCombinedInputNotActive) {
      this._catCombinedInputNotActive.tabIndex = 0;
    }

    /**
     * If start input is active, show start calendar
     */

    if (this._catDateStartInput?.isActive) {
      this.setTooltipDisplay(false);

      if (this._catDateStartCalendarPopup) {
        this._catDateStartCalendarPopup.hidden = false;
      }

      if (this._catDateEndCalendarPopup) {
        this._catDateEndCalendarPopup.hidden = true;
      }

      // Focus on calendar after activated
      setTimeout(() => {
        if (this._catDateStartCalendarPopup) {
          this._catDateStartCalendarPopup.tabIndex = 0;
          this._catDateStartCalendarPopup?.focus();
        }
      }, 100);
    }

    /**
     * If end input is active, show end calendar
     */
    if (this._catDateEndInput?.isActive) {
      this.setTooltipDisplay(false);

      if (this._catDateStartCalendarPopup) {
        this._catDateStartCalendarPopup.hidden = true;
      }

      if (this._catDateEndCalendarPopup) {
        this._catDateEndCalendarPopup.hidden = false;
      }

      // Focus on calendar after activated
      setTimeout(() => {
        if (this._catDateEndCalendarPopup) {
          this._catDateEndCalendarPopup.tabIndex = 0;
          this._catDateEndCalendarPopup.focus();
        }
      }, 100);
    }

    // Clear cancelled state
    this.isCancelled = false;
    setTimeout(() => {
      if (this.selectedStartTime) {
        this._catDateStartCalendarTimeList?.shadowRoot?.querySelector<HTMLButtonElement>('.is-active')?.focus();
      }
      if (this.selectedEndTime) {
        this._catDateEndCalendarTimeList?.shadowRoot?.querySelector<HTMLButtonElement>('.is-active')?.focus();
      }
    }, 100);

    // Focus on the active date if the start value available in start date calendar
    if (this.startValue) {
      setTimeout(() => {
        const element = this._catStartCalender?.shadowRoot?.querySelector<HTMLButtonElement>('.is-active');
        if (element) {
          this._catStartCalender?.setFocusOnDate(element);
        }
      }, 100);
    }

    // Focus on the active date if the end value available in end date calendar
    if (this.endValue) {
      setTimeout(() => {
        const element = this._catEndCalender?.shadowRoot?.querySelector<HTMLButtonElement>('.is-active');
        if (element) {
          this._catEndCalender?.setFocusOnDate(element);
        }
      }, 100);
    }

    this.emitPanelOpenEvent();

    // Close month popup on toggle if it is open
    if (this._catStartCalender?.showMonthPopup) {
      this._catStartCalender.showMonthPopup = false;
    }
    if (this._catEndCalender?.showMonthPopup) {
      this._catEndCalender.showMonthPopup = false;
    }
  };

  /**
   * Show or hide the tooltip when label overflow
   */
  private displayStartTooltip(enableTooltip: boolean) {
    if (this.labelOverflowStartTooltip) {
      this.labelOverflowStartTooltip.enableTooltip = enableTooltip;
    }
  }

  /**
   * Show or hide the tooltip when label overflow
   */
  private displayEndTooltip(enableTooltip: boolean) {
    if (this.labelOverflowEndTooltip) {
      this.labelOverflowEndTooltip.enableTooltip = enableTooltip;
    }
  }

  /**
   * Emit open event when panel is opened
   */
  private emitPanelOpenEvent() {
    this.dispatch({ eventName: 'bl-date-range-open', detailObj: { isActive: true } }) as BlocksOpenEvent;
  }

  /**
   * Emit close event when panel is closed
   */
  private emitPanelCloseEvent() {
    this.dispatch({ eventName: 'bl-date-range-close', detailObj: { isActive: false } }) as BlocksCloseEvent;
  }

  /**
   * Connected callback lifecycle
   * 1) Add event listeners
   */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this.handleOnClickOutside, false);
    window.addEventListener('resize', this.onResize);
    this.fieldId = this.fieldId || nanoid();
    this.onResize();
    if (this.disabledMaxDate) {
      this._startMax = this.disabledMaxDate;
      this._endMax = this.disabledMaxDate;
    }
    if (this.disabledMinDate) {
      this._startMin = this.disabledMinDate;
      this._endMin = this.disabledMinDate;
    }
  }

  /**
   * Disconnected callback lifecycle
   * 1) Remove event listeners
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this.handleOnClickOutside, false);
    window.removeEventListener('resize', this.onResize);
    this._focusTrap?.deactivate();
  }

  /**
   * firstUpdated lifecycle
   * 1) Add keydown event listener to the input field
   */
  firstUpdated() {
    if (this.showDateRangePicker) {
      this.dynamicPosition();
    }
  }

  /**
   * Handle click outside the component
   * 1) Close the show hide panel on click outside
   * 2) If the calendar is already closed then we don't care about outside clicks and we
   * can bail early
   * 3) By the time a user clicks on the page the shadowRoot will almost certainly be
   * defined, but TypeScript isn't that trusting and sees this.shadowRoot as possibly
   * undefined. To work around that we'll check that we have a shadowRoot (and a
   * rendered .host) element here to appease the TypeScript compiler. This should never
   * actually be shown or run for a human end user.
   * 4) Check to see if we clicked inside the active calendar item
   * 5) If the calendar is active and we've clicked outside of the calendar then it should
   * be closed.
   */
  private handleOnClickOutside = (e: MouseEvent) => {
    /* 2 */
    if (!this.showDateRangePicker) {
      return;
    }

    /* 3 */
    if (!this.shadowRoot?.host) {
      throw Error('Could not determine navigation context during click handler');
    }

    /* 4 */
    const didClickInside = e.composedPath().includes(this.shadowRoot.host);

    /* 5 */

    if (this.showDateRangePicker && !didClickInside) {
      this.setTooltipDisplay(true);

      if (this.showFooter) {
        this.cancelSubmission();
      } else {
        this.showDateRangePicker = false;
        this.hidePopup();
        this.removeActiveState();
      }
    }
  };

  /**
   * Handle keydown - Close the calendar popup when escape is hit
   * 1) Set the focus on the active combined input item on press of the escape key
   */
  private handleCloseKeyDown(e: KeyboardEvent) {
    /* 1 */
    if (e.key === KEYS.DOWN_ARROW || e.key === KEYS.UP_ARROW) {
      e.preventDefault();

      if (this._catDateStartInput?.isActive) {
        this.showDateRangePicker && this._catStartCalender?.focusOnFirstKeypress(e);
        this.showDateRangePicker && this._catDateStartCalendarPopup?.removeAttribute('tabindex');
      }

      if (this._catDateEndInput?.isActive) {
        this.showDateRangePicker && this._catEndCalender?.focusOnFirstKeypress(e);
        this.showDateRangePicker && this._catDateEndCalendarPopup?.removeAttribute('tabindex');
      }
    }

    if (e.key === KEYS.ESCAPE) {
      this.setTooltipDisplay(true);

      this.showDateRangePicker = false;
      this.hidePopup();

      /* 1 */
      if (this._catCombinedInputActive) {
        this._catCombinedInputActive?.focus();
      }
    }

    this.setTabindexOnCalendar(e);
  }

  /**
   * Focus on calendar for using the first arrow key press. For arrow keys tabindex required but not for the tab events
   */
  private setTabindexOnCalendar(e: KeyboardEvent) {
    if (this._catDateStartInput?.isActive) {
      if (e.key === KEYS.TAB) {
        this._catDateStartCalendarPopup?.removeAttribute('tabindex');
      }
    }

    if (this._catDateEndInput?.isActive) {
      if (e.key === KEYS.TAB) {
        this._catDateEndCalendarPopup?.removeAttribute('tabindex');
      }
    }
  }

  /**
   * Handle on input keydown
   * 1) If key selected is enter or spacebar, toggle the calendar open/close
   */
  private handleToggleKeyDown(e: KeyboardEvent) {
    if ((e.key === KEYS.ENTER || e.key === KEYS.SPACE) && !this.readonly) {
      this.toggleDateRangePicker();
    }
  }

  /**
   * Handle dynamic placement for calendar popup
   */
  private dynamicPosition() {
    setTimeout(() => {
      if (this._catDateCalendarPopup) {
        this.autoPositionController.openOverlay();
      }
    }, 1);
  }

  /**
   * Hide calender popup
   */
  private hidePopup() {
    this.autoPositionController.closeOverlay();
    this.updateCalendarGrid();
    this.emitPanelCloseEvent();
    this._focusTrap?.deactivate();
    if (this._catCombinedInputActive && this._catCombinedInputFields) {
      this._catCombinedInputFields[0]?.shadowRoot?.querySelector<HTMLInputElement>('.cat-c-input-field__input')?.focus();
    }
    // Clear the calender and time list values
    if (!this.startValue) {
      this._catStartCalender?.hidePopup();
    }
    this._catStartTimeList?.resetFocusToFirstTime();
    if (!this.endValue) {
      this._catEndCalender?.hidePopup();
    }
    this._catEndTimeList?.resetFocusToFirstTime();
    this.setTooltipDisplay(true);
  }
  /**
   * convertTime - Formats a 12 hour time string into 24 hour format
   * @param {string} timeStr - accepts a 12 hour time string
   * @example convertTime('12:00 AM')
   * @returns // '00:00'
   */
  private convertTime(timeStr: string): string | undefined {
    if (!timeStr) {
      return;
    }
    const [time, modifier] = timeStr.split(' ');
    // eslint-disable-next-line prefer-const
    let [hours, minutes] = time.split(':');
    if (hours === '12' && modifier === 'AM') {
      hours = '00';
    }
    if (modifier === 'PM') {
      hours = parseInt(hours, 10) + 12 + '';
    }

    return `${hours}:${minutes}`;
  }

  /**
   * Close datepicker when Cancel button is clicked
   */
  private cancelSubmission() {
    this.showDateRangePicker = false;

    /**
     * If start value exists, save it and return to that value if window is cancelled
     */

    if (this.originalStartValue && this.originalStartValueRaw && this.showFooter) {
      // Update the selected start date value to be the original one from last applied
      this.startValue = this.originalStartDateFormatted;

      // Update the stored raw date value to be used when passing dates that need other formats
      this.startValueRaw = this.originalStartValueRaw;

      this._catStartCalender!.selectedDate = this.originalStartValueRaw;

      // Make the start input value revert back to the originally applied date
      if (this._catDateStartInput && this.startValueRaw) {
        this._catDateStartInput.value = format(this.startValueRaw, this.dateFormat);
      }

      // Pass the setActiveDate value to the calendar component property to update the calendar date
      if (this.startValueRaw) {
        this.setActiveStartDate = format(this.startValueRaw, this.dateFormat, { locale: enUS }); //Calendar input
      }

      // Pass the setActiveTime value to the time selector component property to update the start time
      if (this.originalStartTime) {
        this.setActiveStartTime = format(this.originalStartTime, 'HH:mm');
      }

      // Reset the disabledMinDate to disable end dated that come before the updated start date
      if (this.startValueRaw) {
        this._endMin = format(this.startValueRaw, this.isoFormat, { locale: enUS });
      }

      this.isCancelled = true;
    } else {
      // If no stored value, reset everything
      if (!(!this.showFooter && this._catDateStartInput?.value && this._catDateEndInput?.value)) {
        if (!this.originalStartValue) {
          if (this._catDateStartInput) {
            this._catDateStartInput.value = '';
          }

          this.setActiveStartDate = null;
          this.setActiveStartTime = null;
          this.startValue = null;
          this.startValueRaw = null;
          this.selectedStartTime = null;
          this.isCancelled = true;
        }
      }
    }

    /**
     * If end value exists, save it and return to that value if window is cancelled
     */
    if (this.originalEndValue && this.originalEndValueRaw && this.showFooter) {
      // Update the selected end date value to be the original one from last applied
      this.endValue = this.originalEndDateFormatted;

      // Update the stored raw date value to be used when passing dates that need other formats
      this.endValueRaw = this.originalEndValueRaw;

      this._catEndCalender!.selectedDate = this.originalEndValueRaw;

      // Make the end input value revert back to the originally applied date
      if (this._catDateEndInput) {
        this._catDateEndInput.value = format(this.originalEndValueRaw, this.dateFormat);
      }

      // Pass the setActiveTime value to the time selector component property to update the end time
      if (this.originalEndTime) {
        this.setActiveEndTime = format(this.originalEndTime, 'HH:mm');
      }

      // Pass the setActiveDate value to the calendar component property to update the calendar date
      if (this.endValueRaw) {
        this.setActiveEndDate = format(this.endValueRaw, this.dateFormat, { locale: enUS });
      }

      this.isCancelled = true;
    } else {
      // If no stored value, reset everything
      if (!(!this.showFooter && this._catDateEndInput?.value && this._catDateStartInput?.value)) {
        if (!this.originalEndValue) {
          if (this._catDateEndInput) {
            this._catDateEndInput.value = '';
          }

          this.setActiveEndDate = null;
          this.setActiveEndTime = null;
          this.selectedEndTime = null;
          this.endValue = null;
          this.endValueRaw = null;
          this.isCancelled = true;
        }
      }
    }
    this.hidePopup();
    this.checkApply();
    this.removeActiveState();
  }

  /**
   * remove Active state on corresponding input field.
   */
  private removeActiveState() {
    if (this._catDateStartInput) {
      this._catDateStartInput.isActive = false;
    }

    if (this._catDateEndInput) {
      this._catDateEndInput.isActive = false;
    }
  }

  /**
   * Apply the values and close datepicker
   */
  private submitForm() {
    if (!this.disabledSubmit) {
      this.showDateRangePicker = false;
      this.hidePopup();
      this.isCancelled = false;

      /**
       * If the picker is cancelled and a start value is present, go back to the original start value
       */
      if (this._catDateStartInput?.value) {
        this.originalStartValue = this._catDateStartInput.value;
        this.originalStartValueRaw = this.startValueRaw;
        this.originalStartTime = this.selectedStartTime;
        if (this.startValueRaw) {
          this.originalStartDateFormatted = format(this.startValueRaw, this.dateFormat, { locale: enUS });
          this.setActiveStartDate = format(this.startValueRaw, this.dateFormat, { locale: enUS });
        }
      }

      /**
       * If the picker is cancelled and a end value is present, go back to the original end value
       */
      if (this._catDateEndInput?.value) {
        this.originalEndValue = this._catDateEndInput.value;
        this.originalEndValueRaw = this.endValueRaw;
        this.originalEndTime = this.selectedEndTime;
        if (this.endValueRaw) {
          this.originalEndDateFormatted = format(this.endValueRaw, this.dateFormat, { locale: enUS });
          this.setActiveEndDate = format(this.endValueRaw, this.dateFormat, { locale: enUS });
        }
      }
    }
    this.dispatchDateRangeEvent();
    this.removeActiveState();
  }

  private formatStartDate(): string {
    return (
      format(this.originalStartValueRaw ?? '', this.dateFormat) +
      (this.originalStartTime ? ` ${format(this.originalStartTime, this.timeFormat)}` : '')
    );
  }

  private formatEndDate(): string {
    return (
      format(this.originalEndValueRaw ?? '', this.dateFormat) + (this.originalEndTime ? ` ${format(this.originalEndTime, this.timeFormat)}` : '')
    );
  }

  private formatOriginalStartValue(): string {
    return format(this.originalStartValueRaw ?? '', this.dateFormat);
  }

  private formatOriginalEndValue(): string {
    return format(this.originalEndValueRaw ?? '', this.dateFormat);
  }

  /**
   * Check whether or not to enable the "Apply" button
   */
  private checkApply() {
    /**
     * Logic to apply whether or not a time selector is present
     */
    if (this.showTimeSelector) {
      /**
       * If both start and end fields have values, enable the apply button
       */
      if (
        this._catDateStartInput?.value &&
        this.startValueRaw &&
        this.selectedStartTime &&
        this._catDateEndInput?.value &&
        this.endValueRaw &&
        this.selectedEndTime
      ) {
        let disabledSubmit = false;

        if (
          !this.originalStartValueRaw ||
          !this.originalEndValueRaw ||
          !this.originalEndTime ||
          !this.originalStartTime ||
          this._catDateStartInput.value !== this.formatStartDate() ||
          this._catDateEndInput.value !== this.formatEndDate()
        ) {
          disabledSubmit = false;
        } else if (this._catDateStartInput.value === this.formatStartDate() || this._catDateEndInput.value === this.formatEndDate()) {
          disabledSubmit = true;
        }

        this.disabledSubmit = disabledSubmit;
      } else {
        this.disabledSubmit = true;
      }
    } else {
      /**
       * If both start and end fields have values, enable the apply button
       */
      if (this._catDateStartInput?.value && this.startValueRaw && this._catDateEndInput?.value && this.endValueRaw) {
        let disabledSubmit = false;
        if (
          !this.originalStartValueRaw ||
          !this.originalEndValueRaw ||
          this._catDateStartInput.value !== this.formatOriginalStartValue() ||
          this._catDateEndInput.value !== this.formatOriginalEndValue()
        ) {
          disabledSubmit = false;
        } else if (
          this._catDateStartInput.value === this.formatOriginalStartValue() ||
          this._catDateEndInput.value === this.formatOriginalEndValue()
        ) {
          disabledSubmit = true;
        }

        this.disabledSubmit = disabledSubmit;
      } else {
        this.disabledSubmit = true;
      }
    }
  }

  /**
   * @fires bl-date-range-change
   */
  private async dispatchDateRangeEvent(): Promise<void> {
    await this.updateComplete;
    let detailObj;
    if (this.showTimeSelector) {
      const time = this.selectedStartTime && format(this.selectedStartTime, 'HH:mm');
      const start24h = time ?? null;
      const end24h = (this.selectedEndTime && format(this.selectedEndTime, 'HH:mm')) ?? null;
      const startDate =
        this.startValueRaw && start24h ? this.combineDateAndTime(start24h, this.startValueRaw) : this.startValueRaw?.toISOString() || null;
      const endDate = this.endValueRaw && end24h ? this.combineDateAndTime(end24h, this.endValueRaw) : this.endValueRaw?.toISOString() || null;

      detailObj = {
        start: {
          value: this.startValue,
          date: startDate,
          time: start24h
        },
        end: {
          value: this.endValue,
          date: endDate,
          time: end24h
        }
      };
    } else {
      detailObj = {
        start: {
          date: this.startValue || null,
          value:
            (this.timeZone && this.startValueRaw ? new TZDate(this.startValueRaw, this.timeZone).toISOString() : this.startValueRaw?.toISOString()) ||
            null
        },
        end: {
          date: this.endValue || null,
          value:
            (this.timeZone && this.endValueRaw ? new TZDate(this.endValueRaw, this.timeZone).toISOString() : this.endValueRaw?.toISOString()) || null
        }
      };
    }
    this.dispatch({ eventName: 'bl-date-range-change', detailObj }) as BlocksDateRangeEvent;
  }

  private combineDateAndTime(time24h: string, date: Date): string {
    const [hours, minutes] = time24h.split(':');
    const newDateTime = add(date, {
      hours: +hours,
      minutes: +minutes
    }).toISOString();
    return newDateTime;
  }

  /**
   * When calendar start date is changed
   */
  private async dateUpdateStart(e: CustomEvent) {
    // Update the input with the selected value
    if (this._catDateStartInput) {
      this._catDateStartInput.value = format(e.detail.rawDate, this.dateFormat);
    }

    // Set global startValue
    this.startValue = e.detail.value;

    // Save the raw date for date caparisons and formatting
    this.startValueRaw = e.detail.rawDate;

    /**
     * Disable all dates before the selected start date on the end calendar
     */
    this._endMin = format(e.detail.rawDate, this.isoFormat, { locale: enUS });

    /**
     * If there's no time selector, move to the "end" calendar after date selection
     */
    if (!this.showTimeSelector) {
      if (this._catDateStartInput) {
        this._catDateStartInput.isActive = false;
      }

      if (this._catDateEndInput) {
        this._catDateEndInput.isActive = true;
      }

      if (this._catDateStartCalendarPopup) {
        this._catDateStartCalendarPopup.hidden = true;
      }

      if (this._catDateEndCalendarPopup) {
        this._catDateEndCalendarPopup.hidden = false;
      }
    }
    const timeFormat = this.timeFormat;
    /**
     * If the start date is after the selected end date, update the end date to match the start date
     */
    if (this._catDateEndInput?.value && this.startValueRaw && this.endValueRaw && isAfter(this.startValueRaw, this.endValueRaw)) {
      // Update calendar active end date
      if (this._catDateEndInput) {
        this._catDateEndInput.value = format(e.detail.rawDate, this.dateFormat);
      }

      // Set global startValue
      this.endValue = e.detail.value;

      // Save the raw date for date caparisons and formatting
      this.endValueRaw = e.detail.rawDate;
      // Move the calendar to the new month and year
      if (this._catEndCalender) {
        this._catEndCalender.updateGrid(this.startValueRaw);
      }

      /**
       * If an end time is present, keep it and update just the date to the start date
       */

      if (this.selectedEndTime) {
        this._catDateEndInput.value = this._catDateStartInput?.value + ' ' + format(this.selectedEndTime, timeFormat);
      } else if (this._catDateEndInput && this._catDateStartInput) {
        this._catDateEndInput.value = this._catDateStartInput?.value;
      }
    }

    /**
     * If a date and time value are present, keep both when the date is being updated
     */
    if (this.selectedStartTime && this._catDateStartInput?.value && this.startValueRaw) {
      this._catDateStartInput.value = format(this.startValueRaw, this.dateFormat) + ' ' + format(this.selectedStartTime, timeFormat);
    }

    /**
     * If the end input doesn't have a value, set the end calendar to the
     * same month and year as the start value
     */
    if (!this._catDateEndInput?.value && this._catEndCalender && this.startValueRaw) {
      this._catEndCalender.updateGrid(this.startValueRaw);
    }

    if (this.endValue) {
      const element = this._catEndCalender?.shadowRoot?.querySelector<HTMLButtonElement>('.is-active');
      const catCalendarElement = this._catDateEndCalendarPopup?.querySelector<BlocksCalendar>('cat-calendar');
      if (element && catCalendarElement) {
        catCalendarElement.setFocusOnDate(element);
      }
    }

    /**
     * Focus on first available time selector button
     */
    this.focusStartTimeList();

    // Is it ok to submit the timestamps?
    this.checkApply();
    if (!this.showFooter) {
      await this.updateComplete;
      this.dispatchDateRangeEvent();
    }
  }

  private focusStartTimeList() {
    setTimeout(() => {
      if (this.showTimeSelector) {
        if (this._catDateStartCalendarTimeList?.shadowRoot?.querySelector<HTMLButtonElement>('.cat-c-time-selector-list__item button.is-active')) {
          this._catDateStartCalendarTimeList?.shadowRoot
            ?.querySelector<HTMLButtonElement>('.cat-c-time-selector-list__item button.is-active')
            ?.focus();
        } else {
          this._catStartTimeList?.setFocussedTime();
        }
      }
    }, 10);
  }

  /**
   * When calendar end date is changed
   */
  private async dateUpdateEnd(e: CustomEvent) {
    // Update the input with the selected value
    if (this._catDateEndInput) {
      this._catDateEndInput.value = format(e.detail.rawDate, this.dateFormat);
    }

    // Set global endValue
    this.endValue = e.detail.value;

    // Save the raw date for date caparisons and formatting
    this.endValueRaw = e.detail.rawDate;

    /**
     * Disable all dates after the selected end date on the start calendar
     */
    this._startMax = format(e.detail.rawDate, this.isoFormat, { locale: enUS });
    const timeFormat = this.timeFormat;

    /**
     * If the start date is before the selected end date, update the start date to match the end date
     */
    if (this._catDateStartInput?.value && this.endValueRaw && this.startValueRaw && isBefore(this.endValueRaw, this.startValueRaw)) {
      // Update calendar active start date
      if (this._catDateStartInput) {
        this._catDateStartInput.value = format(e.detail.rawDate, this.dateFormat);
      }

      // Set global startValue
      this.startValue = e.detail.value;

      // Save the raw date for date caparisons and formatting
      this.startValueRaw = e.detail.rawDate;

      //Move the calendar to the new month and year
      if (this._catStartCalender) {
        this._catStartCalender.updateGrid(this.endValueRaw);
      }

      /**
       * If an start time is present, keep it and update just the date to the end date
       */
      if (this.selectedStartTime) {
        this._catDateStartInput.value = this._catDateEndInput?.value + ' ' + format(this.selectedStartTime, timeFormat);
      } else if (this._catDateEndInput?.value) {
        this._catDateStartInput.value = this._catDateEndInput?.value;
      }
    }

    /**
     * If a date and time value are present, keep both when the date is being updated
     */
    if (this.selectedEndTime && this._catDateEndInput?.value) {
      if (this.endValueRaw) {
        this._catDateEndInput.value = format(this.endValueRaw, this.dateFormat) + ' ' + format(this.selectedEndTime, timeFormat);
      }
    }

    /**
     * If the start date is equal to the end date, check the times and disable
     * any end times that come before the selected start time
     */
    if (this.showTimeSelector) {
      if (this.startValue === this.endValue && this.selectedStartTime) {
        this.minEndTime = format(this.selectedStartTime, 'HH:mm');
      } else {
        this.minEndTime = null;
      }

      /**
       * Focus on first available time selector button
       */
      setTimeout(() => {
        const activeDate = this._catDateEndCalendarTimeList?.shadowRoot?.querySelector<HTMLButtonElement>(
          '.cat-c-time-selector-list__item button.is-active'
        );
        const availableTime = this._catDateEndCalendarTimeList?.shadowRoot?.querySelector<HTMLButtonElement>(
          '.cat-c-time-selector-list__item button.is-available'
        );
        if (activeDate) {
          activeDate.focus();
        } else if (availableTime) {
          this._catEndTimeList?.setFocusOnTime(availableTime);
        }
      }, 10);
    } else {
      if (this.showFooter) {
        /**
         * Focus on the Apply button once a time is selected
         */
        setTimeout(() => {
          this._catDateApply?.focus();
        }, 1);
      }
    }

    // Is it ok to submit the timestamps?
    this.checkApply();
    if (!this.showFooter) {
      await this.updateComplete;
      this.dispatchDateRangeEvent();
      if (!this.showTimeSelector) {
        this.closeDateRangePickerPanel();
      }
    }
  }

  /**
   * When calendar start time is changed
   */
  private timeUpdateStart(e: CustomEvent) {
    this.selectedStartTime = e.detail.rawDate;
    const timeFormat = this.timeFormat;
    /**
     * If a date value exists, append the time value
     */
    if (this._catDateStartInput?.value && this.startValueRaw) {
      /**
       * Move to the "end" calendar after time selection
       */
      this._catDateStartInput.isActive = false;
      if (this._catDateEndInput) {
        this._catDateEndInput.isActive = true;
      }

      if (this._catDateStartCalendarPopup) {
        this._catDateStartCalendarPopup.hidden = true;
      }

      if (this._catDateEndCalendarPopup) {
        this._catDateEndCalendarPopup.hidden = false;
      }

      const catCalendar = this._catDateEndCalendarPopup?.querySelector('cat-calendar');
      if (!this.endValue) {
        catCalendar?.shadowRoot?.querySelector<HTMLButtonElement>('.cat-c-calendar__month-selector-button')?.focus();
      }

      if (this.endValue) {
        const element = this._catEndCalender?.shadowRoot?.querySelector<HTMLButtonElement>('.is-active');

        if (element) {
          catCalendar?.setFocusOnDate(element);
        }
      }

      // Append time value to existing date
      if (this.selectedStartTime) {
        this._catDateStartInput.value = format(this.startValueRaw, this.dateFormat) + ' ' + format(this.selectedStartTime, timeFormat);
      }

      // Is it ok to submit the timestamp?
      this.checkApply();
    } else if (this._catDateStartInput && this.selectedStartTime) {
      this._catDateStartInput.value = format(this.selectedStartTime, timeFormat);
    }
    if (!this.showFooter) {
      this.dispatchDateRangeEvent();
    }
  }

  /**
   * When calendar end time is changed
   */
  private timeUpdateEnd(e: CustomEvent) {
    this.selectedEndTime = e.detail.rawDate;
    const timeFormat = this.timeFormat;
    /**
     * If a date value exists, append the time value
     */
    if (this._catDateEndInput?.value && this.endValueRaw) {
      // Append time value to existing date
      if (this.selectedEndTime) {
        this._catDateEndInput.value = format(this.endValueRaw, this.dateFormat) + ' ' + format(this.selectedEndTime, timeFormat);
      }

      // Is it ok to submit the timestamp?
      this.checkApply();
      /**
       * Focus on the Apply button once a time is selected
       */
      setTimeout(() => {
        this._catDateApply?.focus();
      }, 1);
    } else if (this._catDateEndInput && this.selectedEndTime) {
      this._catDateEndInput.value = format(this.selectedEndTime, timeFormat);
    }

    if (!this.showFooter) {
      this.dispatchDateRangeEvent();
      this.closeDateRangePickerPanel();
    }
  }

  /**
   * Resize Handler
   */
  private onResize = () => {
    if (window.innerWidth < 480) {
      this.isSmallScreen = true;
    } else {
      this.isSmallScreen = false;
    }
  };

  protected async updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): Promise<void> {
    await this.updateComplete;
    if (_changedProperties.has('locale')) {
      const locale = this.locale;
      setDefaultOptions({ locale });
      if (this.setActiveEndDate) {
        this.setEndDate();
      }
      if (this.setActiveStartDate) {
        this.setStartDate();
      }
      if (this.setActiveStartTime) {
        this.setStartTime();
      }
      if (this.setActiveEndTime) {
        this.setEndTime();
      }
    }
    if (_changedProperties.has('setActiveEndDate') && this.setActiveEndDate != _changedProperties.get('setActiveEndDate')) {
      if (this.setActiveEndDate) {
        this.setEndDate();
      } else {
        /**
         * When user needs to clear the selected DateRange, clear the input field and
         *  originalEndValue and reset everything using isCancelled.
         */
        if (this._catDateEndInput) {
          this._catDateEndInput.value = '';
        }

        this.endValue = null;
        this.endValueRaw = null;
        this.selectedEndTime = null;
        this.originalEndValue = null;
        this.originalEndValueRaw = null;
        this.originalEndTime = null;
        this.isCancelled = true;
        this.checkApply();
      }
    }
    if (_changedProperties.has('setActiveStartDate') && this.setActiveStartDate != _changedProperties.get('setActiveStartDate')) {
      if (this.setActiveStartDate) {
        this.setStartDate();
      } else {
        /**
         * When user needs to clear the selected DateRange, clear the input field and
         *  originalStartValue and reset everything using isCancelled.
         */
        if (this._catDateStartInput) {
          this._catDateStartInput.value = '';
        }

        this.startValue = null;
        this.startValueRaw = null;
        this.selectedStartTime = null;
        this.originalStartValue = null;
        this.originalStartValueRaw = null;
        this.originalStartTime = null;
        this.isCancelled = true;
        this.checkApply();
      }
    }
    if (
      (_changedProperties.has('setActiveStartTime') && this.setActiveStartTime != _changedProperties.get('setActiveStartTime')) ||
      (this.setActiveStartTime && this.isCancelled)
    ) {
      if (this.setActiveStartTime) {
        this.setStartTime();
      }
    }
    if (
      (_changedProperties.has('setActiveEndTime') && this.setActiveEndTime != _changedProperties.get('setActiveEndTime')) ||
      (this.setActiveEndTime && this.isCancelled)
    ) {
      if (this.setActiveEndTime) {
        this.setEndTime();
      }
    }
    if (_changedProperties.has('disabledMaxDate')) {
      this._startMax = this.disabledMaxDate;
      this._endMax = this.disabledMaxDate;
    }
    if (_changedProperties.has('disabledMinDate')) {
      this._startMin = this.disabledMinDate;
      this._endMin = this.disabledMinDate;
    }

    this.internals_?.setFormValue(JSON.stringify({ start: this.startValueRaw?.toISOString() || null, end: this.endValueRaw?.toISOString() || null }));

    /**To activate focus trap */
    if (this.showDateRangePicker && _changedProperties.has('showDateRangePicker')) {
      this.setFocusTrap();
    }
  }

  private setFocusTrap() {
    const calendarPanel = this._catCalendarPanel;
    if (calendarPanel) {
      this._focusTrap = createFocusTrap(calendarPanel, {
        initialFocus: calendarPanel,
        tabbableOptions: { getShadowRoot: true },
        fallbackFocus: calendarPanel,
        allowOutsideClick: true
      });

      this._focusTrap?.activate();
    }
  }

  private setEndDate() {
    if (this.setActiveEndDate) {
      this.endValueRaw = parse(this.setActiveEndDate, this.dateFormat, new Date(), getTimezoneConfig(this.timeZone));
    }

    if (this.endValueRaw) {
      this.endValue = format(this.endValueRaw, this.dateFormat, { locale: enUS });
      this._startMax = format(this.endValueRaw, this.isoFormat, { locale: enUS });

      if (this._catDateEndInput) {
        this._catDateEndInput.value = format(this.endValueRaw, this.dateFormat);
      }
    }

    this.originalEndValueRaw = this.endValueRaw;
    if (this.selectedEndTime && this._catDateEndInput?.value) {
      this.populateEndOutputField();
    }

    if (this.endValueRaw) {
      this.originalEndDateFormatted = format(this.endValueRaw, this.dateFormat, { locale: enUS });
    }

    this.originalEndValue = this._catDateEndInput?.value;
  }

  private setStartDate() {
    if (this.setActiveStartDate) {
      this.startValueRaw = parse(this.setActiveStartDate, this.dateFormat, new Date(), getTimezoneConfig(this.timeZone));

      this.startValue = format(this.startValueRaw, this.dateFormat, { locale: enUS });
      this._endMin = format(this.startValueRaw, this.isoFormat, { locale: enUS });

      if (this._catDateStartInput) {
        this._catDateStartInput.value = format(this.startValueRaw, this.dateFormat);
      }
    }

    if (this.selectedStartTime && this._catDateStartInput?.value) {
      this.populateStartOutputField();
    }
    this.originalStartValueRaw = this.startValueRaw;
    if (this.startValueRaw) {
      this.originalStartDateFormatted = format(this.startValueRaw, this.dateFormat, { locale: enUS });
    }

    this.originalStartValue = this._catDateStartInput?.value;
  }

  private setStartTime() {
    this.populateStartOutputField();
    this.originalStartTime = this.selectedStartTime;
    this.originalStartValue = this._catDateStartInput?.value;
  }

  private setEndTime() {
    this.populateEndOutputField();
    this.originalEndValue = this._catDateEndInput?.value;
    this.originalEndTime = this.selectedEndTime;
  }

  protected willUpdate(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('setActiveEndTime')) {
      if (this.setActiveEndTime) {
        const date = getConvertedDate(this.timeZone);
        const convertedTime = this.convertTime(this.setActiveEndTime);
        if (convertedTime) {
          const [hours, minutes] = convertedTime.split(':');
          date.setHours(+hours);
          date.setMinutes(+minutes);
          this.selectedEndTime = date;
        }
      } else {
        this.selectedEndTime = null;
        this.originalEndTime = null;
        this.isCancelled = true;
        this.populateEndOutputField();
        this.checkApply();
      }
    }
    if (_changedProperties.has('setActiveStartTime')) {
      if (this.setActiveStartTime) {
        const date = getConvertedDate(this.timeZone);
        const convertedTime = this.convertTime(this.setActiveStartTime);
        if (convertedTime) {
          const [hours, minutes] = convertedTime.split(':');
          date.setHours(+hours);
          date.setMinutes(+minutes);
          this.selectedStartTime = date;
        }
      } else {
        this.selectedStartTime = null;
        this.originalStartTime = null;
        this.isCancelled = true;
        this.populateStartOutputField();
        this.checkApply();
      }
    }
    if (_changedProperties.has('showDateRangePicker') && this.showDateRangePicker) {
      if (this.showFooter) {
        this.checkApply();
      }
    }
    if (_changedProperties.has('disabledMaxDate')) {
      this._startMax = this.disabledMaxDate;
      this._endMax = this.disabledMaxDate;
    }
    if (_changedProperties.has('disabledMinDate')) {
      this._startMin = this.disabledMinDate;
      this._endMin = this.disabledMinDate;
    }
  }

  /**
   * Handle click outside the component
   * 1) In start calendar , when there is already date set/selected and user navigates to another month/year and closes the panel. In such cases the grid should
     display the selected date when re-opened.
   * 2) In End calendar , when there is already date set/selected and user navigates to another month/year and closes the panel. In such cases the grid should
     display the selected date when re-opened.
   * 3)Irrespective of whether it is start calendar/end calendar, when disabledMin and Max date is set and user navigates to another month/year and closes the panel.In such cases the grid should
     display the disabledMin date when re-opened. Now if there is any date explicitly set from user, then grid should display only the date selected.
   */
  private updateCalendarGrid() {
    const startCalendar = this._catStartCalender;
    const endCalendar = this._catEndCalender;
    const currentStartMonth = startCalendar?.navDate?.getMonth();
    const currentStartYear = startCalendar?.navDate?.getFullYear();
    const currentEndMonth = endCalendar?.navDate?.getMonth();
    const currentEndYear = endCalendar?.navDate?.getFullYear();

    if (startCalendar && endCalendar) {
      const currentStartDate = parse(
        this.startValue || format(startCalendar.currentDate, this.dateFormat, { locale: enUS }),
        this.dateFormat,
        new Date(),
        getTimezoneConfig(this.timeZone)
      );

      const activeStartMonth = currentStartDate?.getMonth();
      const activeStartYear = currentStartDate?.getFullYear();
      const currentEndDate = parse(
        this.endValue || format(endCalendar.currentDate, this.dateFormat, { locale: enUS }),
        this.dateFormat,
        new Date(),
        getTimezoneConfig(this.timeZone)
      );

      const activeEndMonth = currentStartDate?.getMonth();
      const activeEndYear = currentStartDate?.getFullYear();

      /* 1 */
      if (
        ((currentStartMonth != activeStartMonth || currentStartYear != activeStartYear) && this.setActiveStartDate && this.showFooter) ||
        (!this.showFooter && !isEqual(startCalendar?.navDate ?? '', currentStartDate))
      ) {
        startCalendar.updateGrid(currentStartDate);
      }

      /* 2 */
      if (
        ((currentEndMonth != activeEndMonth || currentEndYear != activeEndYear) && this.setActiveEndDate && this.showFooter) ||
        (!this.showFooter && !isEqual(endCalendar?.navDate ?? '', currentEndDate))
      ) {
        endCalendar.updateGrid(currentEndDate);
      }

      /* 3 */
      if (isEqual(currentStartDate, currentEndDate) && !(this.startValue && this.endValue)) {
        if (this._startMax && this._startMin && this._endMax && this._endMin) {
          if (this.disabledMinDate) {
            const minDates = parse(this.disabledMinDate, 'yyyy/MM/dd', new Date(), getTimezoneConfig(this.timeZone));
            startCalendar.updateGrid(minDates);
            endCalendar.updateGrid(minDates);
          }
        } else {
          startCalendar.updateGrid(startCalendar.currentDate);
          endCalendar.updateGrid(endCalendar.currentDate);
        }
      }
    }
  }

  private populateStartOutputField(): void {
    if (this._catDateStartInput) {
      if (this.startValueRaw && this.selectedStartTime) {
        this._catDateStartInput.value = format(this.startValueRaw, this.dateFormat) + ' ' + format(this.selectedStartTime, this.timeFormat);
      } else if (this.selectedStartTime) {
        this._catDateStartInput.value = format(this.selectedStartTime, this.timeFormat);
      } else {
        this._catDateStartInput.value = '';
      }
    }
  }

  private populateEndOutputField(): void {
    if (this._catDateEndInput) {
      if (this.endValueRaw && this.selectedEndTime) {
        this._catDateEndInput.value = format(this.endValueRaw, this.dateFormat) + ' ' + format(this.selectedEndTime, this.timeFormat);
      } else if (this.selectedEndTime) {
        this._catDateEndInput.value = format(this.selectedEndTime, this.timeFormat);
      } else {
        this._catDateEndInput.value = '';
      }
    }
  }

  /**
   * close the panel when both the input values are given and there is no footer
   */
  private closeDateRangePickerPanel() {
    if (!this.showTimeSelector) {
      if (this.hasStartAndEndDates) {
        this.showDateRangePicker = false;
        this.hidePopup();
        this.removeActiveState();
      }
    } else {
      if (this.hasDateAndTime) {
        this.showDateRangePicker = false;
        this.hidePopup();
        this.removeActiveState();
      }
    }
  }

  private hasFieldNote() {
    return (
      this.fieldNote ||
      this.errorNote ||
      this.warningNote ||
      this.slotNotEmpty('field-note') ||
      this.slotNotEmpty('error') ||
      this.slotNotEmpty('warning')
    );
  }

  private renderLoading(componentClassNames: DirectiveResult<typeof ClassMapDirective>) {
    return html`
      <div part="base" class="${componentClassNames}">
        <div part="loader-container" class="cat-c-daterangepicker__input-loader-container">
          <div part="loader-wrapper" class="cat-c-daterangepicker__input-loader-wrapper">
            ${this.size === 'md' && !this.hideLabel
              ? html` <span part="label-loader" class="cat-c-daterangepicker__medium-label"
                  ><cat-skeleton
                    part="label-skeleton"
                    exportparts=${this.getExportParts('skeleton-label', 'skeleton')}
                    class="class3"
                    size="sm"
                    width="128px"
                  ></cat-skeleton>
                </span>`
              : nothing}

            <div part="body-loader" class="cat-c-daterangepicker__input-loader">
              <cat-skeleton part="body-skeleton" exportparts=${this.getExportParts('skeleton-body', 'skeleton')}> </cat-skeleton>
              <cat-icon-calendar
                part="icon-calendar"
                exportparts=${this.getExportParts('icon-calendar', 'icon')}
                class="cat-c-daterangepicker__icon-loader"
                slot="after"
              ></cat-icon-calendar>
            </div>
          </div>
          <div part="loader-wrapper" class="cat-c-daterangepicker__input-loader-wrapper">
            ${this.size === 'md' && !this.hideLabel
              ? html` <span part="label-loader" class="cat-c-daterangepicker__medium-label"
                  ><cat-skeleton
                    part="label-skeleton"
                    exportparts=${this.getExportParts('skeleton-label', 'skeleton')}
                    class="class3"
                    size="sm"
                    width="128px"
                  ></cat-skeleton>
                </span>`
              : nothing}

            <div part="body-loader" class="cat-c-daterangepicker__input-loader">
              <cat-skeleton part="body-skeleton" exportparts=${this.getExportParts('body-skeleton', 'skeleton')}> </cat-skeleton>
              <cat-icon-calendar
                part="icon-calendar"
                exportparts=${this.getExportParts('icon-calendar', 'icon')}
                class="cat-c-daterangepicker__icon-loader"
                slot="after"
              ></cat-icon-calendar>
            </div>
          </div>
        </div>

        ${this.hasFieldNote()
          ? html`<div part="field-note-loader" class="cat-c-daterangepicker__fieldnote-loader">
              <cat-skeleton
                part="field-note-skeleton"
                exportparts=${this.getExportParts('skeleton-field-note', 'skeleton')}
                size="sm"
                width="128px"
              ></cat-skeleton>
            </div>`
          : nothing}
      </div>
    `;
  }

  private setAriaDescribedBy() {
    if (this.hasFieldNote()) {
      this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
    }
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-daterangepicker-field', {
      'cat-c-daterangepicker-field--hidden-label': this.hideLabel,
      'cat-c-daterangepicker-field--with-timeselector': this.showTimeSelector,
      'cat-c-daterangepicker-field--small-screen': this.isSmallScreen,
      'cat-c-daterangepicker-field--full-width': this.isFullWidth,
      'cat-c-daterangepicker-field--disabled': this.disabled,
      'cat-c-daterangepicker-field--readonly': this.readonly,
      'cat-c-daterangepicker-field--loader': this.isLoading === true,
      'cat-c-daterangepicker-field--large': this.size === 'lg',
      'cat-c-daterangepicker-field--medium': this.size === 'md',
      'cat-c-daterangepicker-field--small': this.size === 'sm',
      'cat-is-warning': this.status === 'warning',
      'cat-is-error': this.status === 'error'
    });

    if (this.isLoading) {
      return this.renderLoading(componentClassNames);
    }

    this.setAriaDescribedBy();

    return html`
      <div part="base" class="${componentClassNames}">
        <div part="body" class="cat-c-daterangepicker-field__body">
          <cat-combined-input
            part="combined"
            exportparts=${this.getExportParts('combined-input')}
            ?isReadonly=${true}
            ?hideLabel="${this.hideLabel}"
            ?disabled=${this.disabled}
            ?required=${this.required}
            placeholder="${this.placeholder ?? this.dateFormat}"
            startDateLabel="${ifDefined(this.startDateLabel)}"
            @keydown=${this.handleToggleKeyDown}
            endDateLabel="${ifDefined(this.endDateLabel)}"
            @click=${this.toggleDateRangePicker}
            .size=${this.size}
            .status=${this.status}
            .notation=${this.notation}
            tooltipShowDelay=${this.tooltipShowDelay}
            tooltipHideDelay=${this.tooltipHideDelay}
            ?hideLabelTooltip=${this.hideLabelTooltip}
          ></cat-combined-input>
          <div
            part="popup-wrapper"
            class="cat-c-daterangepicker-field__popup-wrapper"
            popover=${ifDefined(this.isPopoverEnabled ? 'manual' : undefined)}
            @keydown=${this.handleCloseKeyDown}
          >
            <div part="popup" class="cat-c-daterangepicker-field__popup" ?hidden="${!this.showDateRangePicker}">
              <div part="popup-body" class="cat-c-daterangepicker-field__popup-body" role="dialog" aria-label=${this.arialabel} aria-modal="true">
                <div part="calendar-start-wrapper" class="cat-c-daterangepicker-field__calendar cat-c-daterangepicker-field__calendar-start">
                  <cat-calendar
                    part="calendar-start"
                    exportparts=${this.getExportParts('calendar-start', 'calendar')}
                    .locale=${this.locale}
                    ?resetDates="${this.isCancelled}"
                    .setActiveDate="${this.startValueRaw ? format(this.startValueRaw!, this.dateFormat, { locale: enUS }) : null}"
                    .disabledMinDate=${this._startMin}
                    .disabledMaxDate=${this._startMax}
                    .multiYear=${this.multiYear}
                    .previousButtonText=${this.previousButtonText}
                    .nextButtonText=${this.nextButtonText}
                    dateFormat=${this.dateFormat}
                    .timeZone=${ifDefined(this.timeZone)}
                    @bl-calendar-change=${this.dateUpdateStart}
                  ></cat-calendar>
                  ${this.showTimeSelector
                    ? html`
                        <div part="time-start-wrapper" class="cat-c-daterangepicker-field__time-selector-container">
                          <cat-time-selector-list
                            part="time-start"
                            exportparts=${this.getExportParts('time-selector-list-start', 'time-selector-list')}
                            placeholder=${ifDefined(this.placeholder)}
                            .locale=${this.locale}
                            @bl-time-change=${this.timeUpdateStart}
                            .orientation=${this.isSmallScreen ? 'horizontal' : undefined}
                            ?resetTime="${this.isCancelled}"
                            .setActiveTime="${this.convertTime(this.setActiveStartTime ?? '') ?? ''}"
                            ?is24HourFormat=${this.is24HourFormat}
                            .timeIncrements=${this.timeIncrements}
                            .timeStart="${this.timeStart}"
                            .timeEnd="${this.timeEnd}"
                            .timeSelectorLabel=${this.timeSelectorLabel}
                          ></cat-time-selector-list>
                        </div>
                      `
                    : html`${nothing}`}
                </div>
                <div part="calendar-end-wrapper" class="cat-c-daterangepicker-field__calendar cat-c-daterangepicker-field__calendar-end" hidden>
                  <cat-calendar
                    part="calendar-end"
                    exportparts=${this.getExportParts('calendar-end', 'calendar')}
                    .locale=${this.locale}
                    ?resetDates="${this.isCancelled}"
                    .setActiveDate="${this.endValueRaw ? format(this.endValueRaw!, this.dateFormat, { locale: enUS }) : null}"
                    .disabledMinDate=${this._endMin}
                    .disabledMaxDate=${this._endMax}
                    .multiYear=${this.multiYear}
                    .previousButtonText=${this.previousButtonText}
                    .nextButtonText=${this.nextButtonText}
                    .dateFormat=${this.dateFormat}
                    dateFormat=${this.dateFormat}
                    .timeZone=${ifDefined(this.timeZone)}
                    @bl-calendar-change=${this.dateUpdateEnd}
                  ></cat-calendar>
                  ${this.showTimeSelector
                    ? html`
                        <div part="time-end-wrapper" class="cat-c-daterangepicker-field__time-selector-container">
                          <cat-time-selector-list
                            part="time-end"
                            exportparts=${this.getExportParts('time-selector-list-end', 'time-selector-list')}
                            placeholder=${ifDefined(this.placeholder)}
                            .locale=${this.locale}
                            @bl-time-change=${this.timeUpdateEnd}
                            orientation=${ifDefined(this.isSmallScreen ? 'horizontal' : undefined)}
                            ?resetTime="${this.isCancelled}"
                            .setActiveTime="${this.convertTime(this.setActiveEndTime ?? '') ?? ''}"
                            .disabledMinTime="${this.minEndTime ?? ''}"
                            ?is24HourFormat=${this.is24HourFormat}
                            .timeIncrements=${this.timeIncrements}
                            .timeStart="${this.timeStart}"
                            .timeEnd="${this.timeEnd}"
                            .timeSelectorLabel=${this.timeSelectorLabel}
                          ></cat-time-selector-list>
                        </div>
                      `
                    : html`${nothing}`}
                </div>
                ${this.showFooter
                  ? html`
                      <div part="footer" class="cat-c-daterangepicker-field__footer">
                        <cat-button
                          part="footer-submit"
                          exportparts=${this.getExportParts('button-footer-submit', 'button')}
                          class="cat-c-daterangepicker-field__footer-submit"
                          @click=${this.submitForm}
                          ?disabled="${this.disabledSubmit}"
                          variant=${ifDefined(this.disabledSubmit ? undefined : 'primary')}
                          >${this.applyLabel}</cat-button
                        >
                        <cat-button
                          part="footer-cancel"
                          exportparts=${this.getExportParts('button-footer-cancel', 'button')}
                          class="cat-c-daterangepicker-field__footer-cancel"
                          @click=${this.cancelSubmission}
                          variant="ghost"
                          >${this.cancelLabel}</cat-button
                        >
                      </div>
                    `
                  : html`${nothing}`}
              </div>
            </div>
          </div>
        </div>
        ${this.fieldNote || this.slotNotEmpty('field-note')
          ? html`
              <cat-field-note
                part="field-note-wrapper"
                exportparts=${this.getExportParts('field-note')}
                id=${ifDefined(this.ariaDescribedBy)}
                .status=${this.status}
                ?isDisabled=${this.disabled}
                class="cat-c-daterangepicker-field__field-note"
              >
                <slot part="part-field-note" name="field-note">${this.fieldNote}</slot>
              </cat-field-note>
            `
          : html`${nothing}`}
        ${this.errorNote || this.slotNotEmpty('error')
          ? html`
              <cat-field-note
                part="field-note-wrapper"
                exportparts=${this.getExportParts('field-note')}
                id=${ifDefined(this.ariaDescribedBy)}
                .status=${'error'}
                ?isDisabled=${this.disabled}
                class="cat-c-daterangepicker-field__field-note"
              >
                <slot part="part-error" name="error">${this.errorNote}</slot>
              </cat-field-note>
            `
          : html`${nothing}`}
        ${this.warningNote || this.slotNotEmpty('warning')
          ? html`
              <cat-field-note
                part="field-note-wrapper"
                exportparts=${this.getExportParts('field-note')}
                id=${ifDefined(this.ariaDescribedBy)}
                ?isDisabled=${this.disabled}
                class="cat-c-daterangepicker-field__field-note"
              >
                <slot part="part-warning" name="warning">${this.warningNote}</slot>
              </cat-field-note>
            `
          : html`${nothing}`}
      </div>
    `;
  }
}

if (customElements.get('cat-daterangepicker-field') === undefined) {
  customElements.define('cat-daterangepicker-field', BlocksDaterangepickerField);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-daterangepicker-field': BlocksDaterangepickerField;
  }
}
